import { useState, useEffect, useCallback } from 'react';

const API_BASE_URL = process.env.REACT_APP_WBC_API_BASE_URL || 'http://localhost:8080';
const QUERY_SERVICE_PATH = process.env.REACT_APP_WBC_QUERY_SERVICE_PATH || '/queryService/api/v1';

/**
 * Convert time range to epoch timestamps
 */
const timeRangeToEpoch = (timeRange) => {
  // Handle custom range objects with exact timestamps
  if (typeof timeRange === 'object' && timeRange.type === 'custom') {
    const fromDate = Math.floor(new Date(timeRange.from).getTime() / 1000);
    const toDate = Math.floor(new Date(timeRange.to).getTime() / 1000);
    
    return {
      fromDate,
      toDate
    };
  }
  
  // Handle preset string ranges
  const now = Math.floor(Date.now() / 1000); // Current time in epoch seconds
  const ranges = {
    '5m': now - 300,
    '15m': now - 900,
    '30m': now - 1800,
    '1h': now - 3600,
    '3h': now - 10800,
    '6h': now - 21600,
    '12h': now - 43200,
    '24h': now - 86400,
    '2d': now - 172800,
    '7d': now - 604800,
    '30d': now - 2592000,
    '90d': now - 7776000
  };
  
  const fromDate = ranges[timeRange] || ranges['24h'];
  
  return {
    fromDate,
    toDate: now
  };
};

// Helper to safely encode values for URL parameters
const safeEncodeValue = (value) => {
  if (!value) return '';
  
  // First decode if already encoded, then encode properly
  try {
    const decoded = decodeURIComponent(value);
    const encoded = encodeURIComponent(decoded);
    return encoded;
  } catch (e) {
    // If decoding fails, assume it's not encoded and encode it
    const encoded = encodeURIComponent(value);
    return encoded;
  }
};

// Helper to safely decode values from API responses
const safeDecodeValue = (value) => {
  if (!value) return '';
  try {
    return decodeURIComponent(value);
  } catch (e) {
    // If decoding fails, return original value
    console.warn(`[useFilters] Failed to decode value: ${value}`);
    return value;
  }
};

// Helper to handle API requests
const fetchApi = async (endpoint, params = {}) => {
  // Build endpoint against gateway host + compatibility path.
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  const baseUrl = API_BASE_URL.replace(/\/$/, '');
  const queryServicePath = QUERY_SERVICE_PATH.startsWith('/') ? QUERY_SERVICE_PATH : `/${QUERY_SERVICE_PATH}`;
  const fullUrl = `${baseUrl}${queryServicePath}/${cleanEndpoint}`;
  const url = new URL(fullUrl);
  
  // Build query parameters
  Object.entries(params).forEach(([key, value]) => {
    // Handle both arrays and primitive values, skip empty arrays
    if (value !== undefined && value !== null && value !== '') {
      if (Array.isArray(value)) {
        if (value.length > 0) {
          const encodedValues = value.map(v => safeEncodeValue(v));
          url.searchParams.append(key, encodedValues.join(','));
        }
      } else {
        // For numbers and strings, add them directly
        url.searchParams.append(key, value);
      }
    }
  });

  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`API error: ${response.status} ${response.statusText}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`[useFilters] Failed to fetch ${endpoint}:`, error);
    return []; // Return empty array on error
  }
};

export const useFilters = (timeRange = '24h') => {
  // State for filter options
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [operators, setOperators] = useState([]);

  // State for selected filter values
  const [selectedMakes, setSelectedMakes] = useState([]);
  const [selectedModels, setSelectedModels] = useState([]);
  const [selectedOperators, setSelectedOperators] = useState([]);

  // State for filtered device IDs
  const [filteredDeviceIds, setFilteredDeviceIds] = useState([]);

  // State to track if initial filter options have been loaded
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  // Loading and error states
  const [loading, setLoading] = useState({
    makes: false,
    models: false,
    operators: false,
    devices: false,
  });
  const [error, setError] = useState(null);

  // Fetch initial filter options and set them as selected by default
  useEffect(() => {
    const fetchAndSetInitialData = async () => {
      setLoading(prev => ({ ...prev, makes: true, models: true, operators: true }));
      try {
        const [makesData, modelsData, operatorsData] = await Promise.all([
          fetchApi('filters/makes'),
          fetchApi('filters/models'),
          fetchApi('filters/operators'),
        ]);

        // Sanitize data to remove null/undefined values and ensure all are strings
        // Apply consistent decoding to all filter data
        const sanitizedMakes = makesData
          .filter(make => make != null && typeof make === 'string')
          .map(make => safeDecodeValue(make));
        const sanitizedModels = modelsData
          .filter(model => model != null && typeof model === 'string')
          .map(model => safeDecodeValue(model));
        const sanitizedOperators = operatorsData
          .filter(operator => operator != null && typeof operator === 'string')
          .map(operator => safeDecodeValue(operator));

        setMakes(sanitizedMakes);
        setModels(sanitizedModels);
        setOperators(sanitizedOperators);

        // By default, all filters are selected, reflecting the "All" state visually
        setSelectedMakes(sanitizedMakes);
        setSelectedModels(sanitizedModels);
        setSelectedOperators(sanitizedOperators);

      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(prev => ({ ...prev, makes: false, models: false, operators: false }));
        setInitialDataLoaded(true); // Signal that initial data is loaded
      }
    };
    fetchAndSetInitialData();
  }, []); // Runs only once on mount

  // Fetch device IDs whenever any filter changes, and on initial load
  useEffect(() => {
    //
    // -- Don't fetch devices until the initial filter options are loaded --
    //
    if (!initialDataLoaded) {
      return; // Exit early if initial data isn't loaded yet
    }

    const fetchDevices = async () => {
      setLoading(prev => ({ ...prev, devices: true }));
      try {
        // Convert time range to epoch timestamps
        const { fromDate, toDate } = timeRangeToEpoch(timeRange);
        
        // Always fetch. If filters are empty, the API will return all devices.
        // Pass the values directly - fetchApi will handle encoding with safeEncodeValue
        const deviceIds = await fetchApi('filters/devices', {
          makes: selectedMakes,
          models: selectedModels,
          operators: selectedOperators,
          fromDate,
          toDate,
        });
        
        // Sanitize device IDs to remove null/undefined values
        const sanitizedDeviceIds = deviceIds.filter(deviceId => deviceId != null && typeof deviceId === 'string');
        setFilteredDeviceIds(sanitizedDeviceIds);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(prev => ({ ...prev, devices: false }));
      }
    };
    fetchDevices();
  }, [selectedMakes, selectedModels, selectedOperators, timeRange, initialDataLoaded]);

  // Handlers for updating selected filters
  const handleMakeChange = useCallback((newMakes) => {
    setSelectedMakes(newMakes);
  }, []);

  const handleModelChange = useCallback((newModels) => {
    setSelectedModels(newModels);
  }, []);

  const handleOperatorChange = useCallback((newOperators) => {
    setSelectedOperators(newOperators);
  }, []);

  return {
    // Filter options
    makes,
    models,
    operators,

    // Selected filter values
    selectedMakes,
    selectedModels,
    selectedOperators,

    // Filtered device IDs
    filteredDeviceIds,

    // Handlers
    handleMakeChange,
    handleModelChange,
    handleOperatorChange,

    // State
    loading,
    error,
  };
};
